/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal/Dropdown
 * @desc Modal component for metadata arising from dropdown
 */

import { MetadataActionCreateDropdown } from 'labstep-web/components/Metadata/Action/Create/Dropdown';
import { MetadataActionCreateModalContainer as MetadataActionCreateModal } from 'labstep-web/components/Metadata/Action/Create/Modal';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Loadable } from 'labstep-web/core/Loadable';
import { PremiumFeatureEmptyState } from 'labstep-web/core/PremiumFeature/EmptyState';
import { useHasFeatureFlagEnabled } from 'labstep-web/hooks/feature-flag';
import { useModalRoute } from 'labstep-web/hooks/modalRoute';
import { Metadata } from 'labstep-web/models/metadata';
import {
  type IMETADATA_PREMIUM_OPTION,
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import { Resource } from 'labstep-web/models/resource.model';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import React, { useState } from 'react';
import type { MetadataActionCreateContainerProps } from '../types';
import type { MetadataActionCreateModalDropdownProps } from './types';

export const MetadataFormCreateBulk = React.lazy(
  () => import('labstep-web/components/Metadata/Form/Create/Bulk'),
);

export const MetadataActionCreateModalDropdown: React.FC<
  MetadataActionCreateModalDropdownProps
> = ({
  entity,
  isTemplate,
  options,
  withVariableFields,
  text,
  dataCy,
  onUnmount,
  initialLabel,
}) => {
  const [selectedMetadataType, setSelectedMetadataType] =
    useState<IMETADATA_PREMIUM_OPTION>({
      label: MetadataTypeValues.default,
      value: MetadataType.default,
      show: true,
    });
  const hasEntityRelationFeatureEnabled =
    useHasFeatureFlagEnabled('entity_relation');

  const [isBulk, setIsBulk] = useState<boolean>(false);
  const { toggleModal: toggleWizardModal } = useModalRoute(
    'metadata-entity-relation-wizard',
  );
  const modalProps: NonNullable<
    MetadataActionCreateContainerProps['modalProps']
  > = {
    viewComponent: ({ toggleModal }) => (
      <MetadataActionCreateDropdown
        trigger={
          <ActionComponent
            dataCy={
              dataCy ||
              'components/Metadata/Action/Create/Modal/Dropdown'
            }
            dataTestId="metadata-action-create-dropdown"
            icon="plus"
            type="text"
            text={
              text ||
              `Add ${Metadata.getHumanReadableEntityName(
                false,
                true,
                entity,
              )}`
            }
          />
        }
        showChemistry={entity instanceof Resource}
        showEntityRelation={
          entity instanceof Resource &&
          entity.is_template &&
          hasEntityRelationFeatureEnabled
        }
        handleOnClickMetadataOption={(metadataType) => {
          if (metadataType.value === MetadataType.entity_relation) {
            // Open wizard instead of regular modal
            toggleWizardModal({
              entityId: entity.idAttr,
              entityName: entity.entityName,
            });
          } else {
            toggleModal();
            setSelectedMetadataType(metadataType);
          }
        }}
        handleOnClickBulkOption={() => {
          toggleModal();
          setIsBulk(true);
        }}
      />
    ),
    onClose: ({ toggleModal }) => {
      toggleModal();
      setIsBulk(false);
    },
    size: isBulk ? 'small' : 'tiny',
  };

  if (
    selectedMetadataType.premiumFeature &&
    !selectedMetadataType.premiumFeatureEnabled
  ) {
    modalProps.header = 'Upgrade Now';
    modalProps.content = (
      <PremiumFeatureEmptyState
        premiumFeature={selectedMetadataType?.premiumFeature}
      />
    );
  }
  if (isBulk) {
    modalProps.content = ({ toggleModal }) => (
      <Loadable>
        <MetadataFormCreateBulk
          entity={entity}
          isTemplate={isTemplate}
          options={enhanceOptions({
            options,
            toast: true,
            onSuccess: ({ response }) => {
              if (options?.onSuccess) {
                options.onSuccess({ response });
              }
              toggleModal();
              setIsBulk(false);
            },
          })}
          withVariableFields={withVariableFields}
        />
      </Loadable>
    );
  }
  if (onUnmount) {
    modalProps.onUnmount = onUnmount;
  }

  return (
    <MetadataActionCreateModal
      isTemplate={isTemplate}
      entity={entity}
      entityName={entity.entityName}
      options={options}
      defaultValues={
        initialLabel && initialLabel.length > 0
          ? { label: initialLabel }
          : undefined
      }
      actionComponentProps={{ type: 'text', icon: 'plus' }}
      withVariableFields={withVariableFields}
      modalProps={modalProps}
      metadataType={selectedMetadataType?.value}
      isHeaderPlural={isBulk}
    />
  );
};
